import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { fetchTenantLogo } from './tenant-logo-api'

interface TenantLogo {
  logoUrl: string | null
  isLoading: boolean
}

/**
 * Active tenant's logo as an object URL (UC-095). The endpoint needs the bearer
 * token, so the image is fetched as a blob instead of used as a plain `src`.
 * The previous object URL is revoked when the logo changes or on unmount.
 */
export function useTenantLogo(tenantId: number | null): TenantLogo {
  const query = useQuery({
    queryKey: ['tenant-logo', tenantId],
    queryFn: ({ signal }) => fetchTenantLogo(signal),
    enabled: tenantId !== null,
    retry: false,
    staleTime: Infinity,
  })
  const blob = query.data ?? null
  const [objectLogo, setObjectLogo] = useState<{ blob: Blob; url: string } | null>(null)

  useEffect(() => {
    if (!blob) return
    const objectUrl = URL.createObjectURL(blob)
    setObjectLogo({ blob, url: objectUrl })
    return () => URL.revokeObjectURL(objectUrl)
  }, [blob])

  // Only the URL built from the current blob counts, so a revoked one is never returned.
  const logoUrl = blob !== null && objectLogo?.blob === blob ? objectLogo.url : null
  // A blob without its URL yet still counts as loading, so the fallback never flashes.
  const isLoading = (tenantId !== null && query.isPending) || (blob !== null && logoUrl === null)
  return { logoUrl, isLoading }
}
